import React from "react";
import { classExpressionSatisfied, OwlClassExpression, ClassHierarchyMap, isSubclassOf, RelationshipTaxonomyNode } from "@semantical/modules/ontology-model";

// Re-export the type for backward compatibility
export type { RelationshipTaxonomyNode };

// Example: A more deeply nested OWL2-style taxonomy
// This demonstrates how relationships could be organized in a real ontology
const exampleDeepTaxonomy: RelationshipTaxonomyNode[] = [
    {
        id: "object-property",
        label: "Object Properties",
        description: "Relationships between individuals",
        icon: <path d="M2,6 L10,6 M7,3 L10,6 L7,9" fill="none" stroke="currentColor" strokeWidth="1.5"/>,
        children: [
            {
                id: "topological",
                label: "Topological",
                description: "Spatial relationships",
                icon: <rect x="1" y="1" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>,
                children: [
                    {
                        id: "mereological",
                        label: "Mereological",
                        description: "Part-whole relationships",
                        icon: <path d="M2,2 L10,2 L10,10 L2,10 Z M4,4 L8,4 L8,8 L4,8 Z" fill="none" stroke="currentColor" strokeWidth="1"/>,
                        children: [
                            {
                                id: "composition",
                                label: "Composition",
                                description: "Essential part-of (lifecycle dependent)",
                                type: "composition",
                                keywords: ["part", "whole", "contains", "owns", "essential"],
                                icon: <path d="M0,6 L6,0 L12,6 L6,12 Z" fill="currentColor"/>
                            },
                            {
                                id: "aggregation",
                                label: "Aggregation",
                                description: "Non-essential part-of (independent lifecycle)",
                                type: "aggregation",
                                keywords: ["part", "group", "collection", "has"],
                                icon: <path d="M0,6 L6,0 L12,6 L6,12 Z" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                            }
                        ]
                    },
                    {
                        id: "containment",
                        label: "Containment",
                        description: "Spatial containment relationships",
                        children: [
                            {
                                id: "contains-spatial",
                                label: "Contains (Spatial)",
                                description: "Spatially contains another element",
                                type: "composition",
                                keywords: ["inside", "within", "encloses"],
                                icon: <g><rect x="1" y="1" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1"/><circle cx="6" cy="6" r="2" fill="currentColor"/></g>
                            }
                        ]
                    }
                ]
            },
            {
                id: "temporal",
                label: "Temporal",
                description: "Time-based relationships",
                icon: <path d="M6,1 L6,6 L9,9" fill="none" stroke="currentColor" strokeWidth="1.5"/>,
                children: [
                    {
                        id: "causal",
                        label: "Causal",
                        description: "Cause-effect relationships",
                        children: [
                            {
                                id: "triggering",
                                label: "Triggering",
                                description: "Source triggers/causes target",
                                type: "triggering",
                                keywords: ["causes", "initiates", "starts", "invokes"],
                                icon: <g><line x1="0" y1="6" x2="6" y2="6" stroke="currentColor" strokeWidth="1.5"/><path d="M6,3 L10,6 L6,9" fill="none" stroke="currentColor" strokeWidth="1.5"/></g>
                            },
                            {
                                id: "influence",
                                label: "Influence",
                                description: "Source influences target (weaker than triggering)",
                                type: "influence",
                                keywords: ["affects", "impacts", "modifies"],
                                icon: <g><line x1="0" y1="6" x2="6" y2="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3,2"/><path d="M6,3 L10,6 L6,9" fill="none" stroke="currentColor" strokeWidth="1.5"/></g>
                            }
                        ]
                    },
                    {
                        id: "sequential",
                        label: "Sequential",
                        description: "Order-based relationships",
                        children: [
                            {
                                id: "flow",
                                label: "Flow",
                                description: "Transfer of information/material",
                                type: "flow",
                                keywords: ["transfer", "data flow", "message", "sends"],
                                icon: <g><line x1="0" y1="6" x2="6" y2="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3,2"/><path d="M6,3 L10,6 L6,9" fill="none" stroke="currentColor" strokeWidth="1.5"/></g>
                            }
                        ]
                    }
                ]
            },
            {
                id: "functional",
                label: "Functional",
                description: "Role and function relationships",
                icon: <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.5"/>,
                children: [
                    {
                        id: "assignment",
                        label: "Assignment",
                        description: "Allocation of behavior to structure",
                        type: "assignment",
                        keywords: ["allocate", "resource", "performs", "executes"],
                        icon: <circle cx="6" cy="6" r="5" fill="currentColor"/>
                    },
                    {
                        id: "serving",
                        label: "Serving",
                        description: "Source provides functionality to target",
                        type: "serving",
                        keywords: ["provides", "supports", "enables", "serves"],
                        icon: <path d="M0,3 L8,6 L0,9" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    },
                    {
                        id: "access",
                        label: "Access",
                        description: "Source reads/writes target data",
                        type: "access",
                        keywords: ["read", "write", "data", "storage"],
                        icon: <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2,2"/>
                    }
                ]
            }
        ]
    },
    {
        id: "taxonomic",
        label: "Taxonomic",
        description: "Classification relationships",
        icon: <path d="M6,0 L6,4 M2,4 L10,4 M2,4 L2,8 M10,4 L10,8" fill="none" stroke="currentColor" strokeWidth="1.5"/>,
        children: [
            {
                id: "specialization",
                label: "Specialization",
                description: "Subtype relationship (is-a)",
                type: "specialization",
                keywords: ["inherits", "extends", "is a", "subtype", "subclass"],
                icon: <path d="M0,3 L8,6 L0,9 Z" fill="none" stroke="currentColor" strokeWidth="1.5"/>
            },
            {
                id: "realization",
                label: "Realization",
                description: "Implementation of specification",
                type: "realization",
                keywords: ["implements", "fulfills", "achieves", "realizes"],
                icon: <g><line x1="0" y1="6" x2="6" y2="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2,2"/><path d="M6,2 L12,6 L6,10 Z" fill="none" stroke="currentColor" strokeWidth="1.5"/></g>
            }
        ]
    },
    {
        id: "associative",
        label: "Associative",
        description: "Generic relationships",
        icon: <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5"/>,
        children: [
            {
                id: "association",
                label: "Association",
                description: "Unspecified relationship",
                type: "association",
                keywords: ["related", "connected", "linked"],
                icon: <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5"/>
            },
            {
                id: "junctions",
                label: "Junctions",
                description: "Logical connectors for combining relationships",
                icon: <circle cx="6" cy="6" r="4" fill="none" stroke="currentColor" strokeWidth="1.5"/>,
                children: [
                    {
                        id: "junction_and",
                        label: "AND Junction",
                        description: "All paths must be followed",
                        type: "junction_and",
                        keywords: ["all", "every", "conjunction", "and"],
                        icon: <circle cx="6" cy="6" r="5" fill="currentColor"/>
                    },
                    {
                        id: "junction_or",
                        label: "OR Junction",
                        description: "One or more paths may be followed",
                        type: "junction_or",
                        keywords: ["any", "one of", "disjunction", "or"],
                        icon: <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    }
                ]
            }
        ]
    }
];

// Render a hierarchical tree item
type TreeItemProps = {
    node: RelationshipTaxonomyNode;
    depth: number;
    expandedIds: Set<string>;
    selectedId: string | null;
    onToggle: (id: string) => void;
    onSelect: (node: RelationshipTaxonomyNode) => void;
    onMouseMove: (e: React.MouseEvent, id: string) => void;
    onRef: (id: string, el: HTMLDivElement | null) => void;
    query: string;
    highlightMatch: (text: string, query: string) => React.ReactNode;
};

function TreeItem({ node, depth, expandedIds, selectedId, onToggle, onSelect, onMouseMove, onRef, query, highlightMatch }: TreeItemProps) {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const isSelected = selectedId === node.id;
    const isSelectable = !!node.type; // In OWL2, nodes with type are selectable even if they have children

    const itemStyle: React.CSSProperties = {
        padding: "6px 12px",
        paddingLeft: 12 + depth * 16,
        cursor: isSelectable ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: isSelected ? "rgba(100, 180, 255, 0.15)" : "transparent",
        borderLeft: isSelected ? "2px solid #6bf" : "2px solid transparent",
        transition: "all 0.1s ease",
        fontSize: isSelectable ? 13 : 12,
        fontWeight: isSelectable ? 400 : 500,
        color: isSelectable ? "#e0e0e0" : "rgba(255,255,255,0.7)"
    };

    const chevronStyle: React.CSSProperties = {
        width: 14,
        height: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.15s ease",
        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
        opacity: hasChildren ? 1 : 0
    };

    const iconStyle: React.CSSProperties = {
        width: 16,
        height: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: isSelectable ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.5)"
    };

    const handleClick = () => {
        // In OWL2, object properties with sub-properties should still be selectable
        // Allow selection if node has a type, regardless of whether it has children
        if (node.type) {
            onSelect(node);
        } else if (hasChildren) {
            onToggle(node.id);
        }
    };

    const handleChevronClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasChildren) {
            onToggle(node.id);
        }
    };

    return (
        <>
            <div
                ref={(el) => onRef(node.id, el)}
                style={itemStyle}
                onClick={handleClick}
                onMouseMove={(e) => onMouseMove(e, node.id)}
            >
                {/* Expand/collapse chevron - clickable separately to allow expand/collapse for selectable parents */}
                <div style={chevronStyle} onClick={handleChevronClick}>
                    {hasChildren && (
                        <svg width="10" height="10" viewBox="0 0 10 10">
                            <path d="M3,1 L7,5 L3,9" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                    )}
                </div>

                {/* Icon */}
                <div style={iconStyle}>
                    <svg width="12" height="12" viewBox="0 0 12 12">
                        {node.icon}
                    </svg>
                </div>

                {/* Label */}
                <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {highlightMatch(node.label, query)}
                </div>

                {/* Leaf indicator / count */}
                {hasChildren && (
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                        {node.children!.length}
                    </span>
                )}
            </div>

            {/* Children */}
            {hasChildren && isExpanded && (
                <div style={{
                    borderLeft: "1px solid rgba(255,255,255,0.1)",
                    marginLeft: 18 + depth * 16
                }}>
                    {node.children!.map(child => (
                        <TreeItem
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            expandedIds={expandedIds}
                            selectedId={selectedId}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            onMouseMove={onMouseMove}
                            onRef={onRef}
                            query={query}
                            highlightMatch={highlightMatch}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

// Get all leaf nodes for search
function getLeafNodes(nodes: RelationshipTaxonomyNode[], path: string[] = []): Array<{node: RelationshipTaxonomyNode; path: string[]}> {
    const result: Array<{node: RelationshipTaxonomyNode; path: string[]}> = [];
    for (const node of nodes) {
        const currentPath = [...path, node.label];
        if (node.type) {
            result.push({ node, path: currentPath });
        }
        if (node.children) {
            result.push(...getLeafNodes(node.children, currentPath));
        }
    }
    return result;
}

// Fuzzy match
function fuzzyMatch(text: string, query: string): { match: boolean; score: number } {
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    if (lowerText === lowerQuery) return { match: true, score: 100 };
    if (lowerText.startsWith(lowerQuery)) return { match: true, score: 90 };
    if (lowerText.includes(lowerQuery)) return { match: true, score: 70 };
    let queryIdx = 0;
    let score = 0;
    for (let i = 0; i < lowerText.length && queryIdx < lowerQuery.length; i++) {
        if (lowerText[i] === lowerQuery[queryIdx]) {
            score += 10;
            queryIdx++;
        }
    }
    if (queryIdx === lowerQuery.length) return { match: true, score };
    return { match: false, score: 0 };
}

/**
 * Check if a class URI is compatible with a class expression constraint.
 * Compatible if:
 * - No constraint (undefined)
 * - No class URI to check (classUri is undefined)
 * - No class hierarchy loaded
 * - The class satisfies the class expression (supports union, intersection, named class)
 */
function isClassCompatible(
    classUri: string | undefined,
    constraint: OwlClassExpression | undefined,
    classHierarchy: ClassHierarchyMap | undefined
): boolean {
    // If no constraint, any class is compatible
    if (!constraint) return true;

    // If no class URI to check, we can't verify - allow it
    if (!classUri) return true;

    // If no class hierarchy loaded, we can't check - allow it
    if (!classHierarchy) return true;

    // Check if class satisfies the constraint expression
    return classExpressionSatisfied(classUri, constraint, (uri, targetUri) =>
        isSubclassOf(uri, targetUri, classHierarchy)
    );
}

/**
 * Check if a relationship node is compatible with both source and target classes.
 * A relationship is compatible if:
 * - Its domain constraint is satisfied by the source class
 * - Its range constraint is satisfied by the target class
 */
function isRelationshipCompatible(
    node: RelationshipTaxonomyNode,
    sourceClassUri: string | undefined,
    targetClassUri: string | undefined,
    classHierarchy: ClassHierarchyMap | undefined
): boolean {
    const domainOk = isClassCompatible(sourceClassUri, node.domain, classHierarchy);
    const rangeOk = isClassCompatible(targetClassUri, node.range, classHierarchy);
    return domainOk && rangeOk;
}

/**
 * Filter a taxonomy tree based on domain and range compatibility.
 * Returns a new tree with only compatible nodes (and their parent categories).
 * Category nodes are kept if they have at least one compatible descendant.
 */
function filterTaxonomyByDomainAndRange(
    nodes: RelationshipTaxonomyNode[],
    sourceClassUri: string | undefined,
    targetClassUri: string | undefined,
    classHierarchy: ClassHierarchyMap | undefined
): RelationshipTaxonomyNode[] {
    if (!classHierarchy) {
        // No filtering if hierarchy is not available
        return nodes;
    }

    const filterNode = (node: RelationshipTaxonomyNode): RelationshipTaxonomyNode | null => {
        // If this is a leaf node (has type), check domain and range compatibility
        if (node.type) {
            return isRelationshipCompatible(node, sourceClassUri, targetClassUri, classHierarchy) ? node : null;
        }

        // If this is a category node, recursively filter children
        if (node.children) {
            const filteredChildren = node.children
                .map(child => filterNode(child))
                .filter((child): child is RelationshipTaxonomyNode => child !== null);

            // Keep category if it has at least one compatible child
            if (filteredChildren.length > 0) {
                return {
                    ...node,
                    children: filteredChildren
                };
            }
        }

        // Category with no compatible children - remove it
        return null;
    };

    return nodes
        .map(node => filterNode(node))
        .filter((node): node is RelationshipTaxonomyNode => node !== null);
}

type Props = {
    x: number;
    y: number;
    taxonomy?: RelationshipTaxonomyNode[];
    onSelect: (node: RelationshipTaxonomyNode) => void;
    onCancel: () => void;
    sourceLabel?: string;
    targetLabel?: string;
    sourceClassUri?: string;  // OWL class URI of the source class for domain filtering
    targetClassUri?: string;  // OWL class URI of the target class for range filtering
    classHierarchy?: ClassHierarchyMap;  // Class hierarchy for domain/range checking
};

type State = {
    query: string;
    selectedId: string | null;
    expandedIds: Set<string>;
    mode: "browse" | "search";
};

export default class RelationshipCommandPaletteHierarchical extends React.Component<Props, State> {
    private inputRef = React.createRef<HTMLInputElement>();
    private containerRef = React.createRef<HTMLDivElement>();
    private listRef = React.createRef<HTMLDivElement>();
    private itemRefs: Map<string, HTMLDivElement> = new Map();
    private taxonomy: RelationshipTaxonomyNode[];
    private leafItems: Array<{node: RelationshipTaxonomyNode; path: string[]}>;
    private lastMousePosition: { x: number; y: number } | null = null;

    constructor(props: Props) {
        super(props);
        const baseTaxonomy = props.taxonomy || exampleDeepTaxonomy;
        // Filter taxonomy based on domain (source) and range (target) constraints
        this.taxonomy = filterTaxonomyByDomainAndRange(baseTaxonomy, props.sourceClassUri, props.targetClassUri, props.classHierarchy);
        this.leafItems = getLeafNodes(this.taxonomy);

        // Start with top-level categories expanded
        const initialExpanded = new Set<string>();
        this.taxonomy.forEach(node => initialExpanded.add(node.id));

        this.state = {
            query: "",
            selectedId: null,
            expandedIds: initialExpanded,
            mode: "browse"
        };
    }

    componentDidMount() {
        setTimeout(() => this.inputRef.current?.focus(), 50);
        document.addEventListener("keydown", this.handleKeyDown);
        document.addEventListener("mousedown", this.handleClickOutside);
        if (this.containerRef.current) {
            this.containerRef.current.addEventListener("wheel", this.handleNativeWheel, { capture: true, passive: false });
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
        document.removeEventListener("mousedown", this.handleClickOutside);
        if (this.containerRef.current) {
            this.containerRef.current.removeEventListener("wheel", this.handleNativeWheel, { capture: true });
        }
    }

    handleNativeWheel = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.listRef.current) {
            this.listRef.current.scrollTop += e.deltaY;
        }
    };

    handleClickOutside = (e: MouseEvent) => {
        if (this.containerRef.current && !this.containerRef.current.contains(e.target as Node)) {
            this.props.onCancel();
        }
    };

    // Get visible items in tree order (respecting expanded state)
    getVisibleItems(): RelationshipTaxonomyNode[] {
        const { query, expandedIds } = this.state;
        const isSearching = query.trim().length > 0;

        if (isSearching) {
            // In search mode, return filtered leaf nodes
            return this.getFilteredLeaves().map(item => item.node);
        }

        // In browse mode, return visible tree items
        const result: RelationshipTaxonomyNode[] = [];
        const addVisible = (nodes: RelationshipTaxonomyNode[]) => {
            for (const node of nodes) {
                result.push(node);
                if (node.children && expandedIds.has(node.id)) {
                    addVisible(node.children);
                }
            }
        };
        addVisible(this.taxonomy);
        return result;
    }

    // Find a node by ID in the taxonomy
    findNodeById(id: string, nodes: RelationshipTaxonomyNode[] = this.taxonomy): RelationshipTaxonomyNode | null {
        for (const node of nodes) {
            if (node.id === id) return node;
            if (node.children) {
                const found = this.findNodeById(id, node.children);
                if (found) return found;
            }
        }
        return null;
    }

    // Find parent of a node
    findParentOf(id: string, nodes: RelationshipTaxonomyNode[] = this.taxonomy, parent: RelationshipTaxonomyNode | null = null): RelationshipTaxonomyNode | null {
        for (const node of nodes) {
            if (node.id === id) return parent;
            if (node.children) {
                const found = this.findParentOf(id, node.children, node);
                if (found !== null) return found;
            }
        }
        return null;
    }

    scrollSelectedIntoView() {
        const { selectedId } = this.state;
        if (selectedId) {
            const el = this.itemRefs.get(selectedId);
            if (el) {
                el.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
        }
    }

    handleKeyDown = (e: KeyboardEvent) => {
        const visibleItems = this.getVisibleItems();
        const { selectedId, expandedIds } = this.state;
        const currentIndex = selectedId ? visibleItems.findIndex(n => n.id === selectedId) : -1;
        const currentNode = selectedId ? this.findNodeById(selectedId) : null;

        switch (e.key) {
            case "Escape":
                e.preventDefault();
                this.props.onCancel();
                break;

            case "ArrowDown":
                e.preventDefault();
                if (visibleItems.length > 0) {
                    const nextIndex = currentIndex < visibleItems.length - 1 ? currentIndex + 1 : 0;
                    this.setState({ selectedId: visibleItems[nextIndex].id }, () => this.scrollSelectedIntoView());
                }
                break;

            case "ArrowUp":
                e.preventDefault();
                if (visibleItems.length > 0) {
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : visibleItems.length - 1;
                    this.setState({ selectedId: visibleItems[prevIndex].id }, () => this.scrollSelectedIntoView());
                }
                break;

            case "ArrowRight":
                e.preventDefault();
                // Expand current node if it has children and is collapsed
                if (currentNode && currentNode.children && !expandedIds.has(currentNode.id)) {
                    this.handleToggle(currentNode.id);
                } else if (currentNode && currentNode.children && expandedIds.has(currentNode.id)) {
                    // Already expanded, move to first child
                    this.setState({ selectedId: currentNode.children[0].id }, () => this.scrollSelectedIntoView());
                }
                break;

            case "ArrowLeft":
                e.preventDefault();
                // Collapse current node if expanded, or go to parent
                if (currentNode && currentNode.children && expandedIds.has(currentNode.id)) {
                    this.handleToggle(currentNode.id);
                } else if (selectedId) {
                    // Go to parent
                    const parent = this.findParentOf(selectedId);
                    if (parent) {
                        this.setState({ selectedId: parent.id }, () => this.scrollSelectedIntoView());
                    }
                }
                break;

            case "Enter":
                e.preventDefault();
                if (currentNode) {
                    if (currentNode.type) {
                        // Selectable node (may have children in OWL2) - select it
                        this.props.onSelect(currentNode);
                    } else if (currentNode.children) {
                        // Pure category node (no type) - toggle expand/collapse
                        this.handleToggle(currentNode.id);
                    }
                }
                break;
        }
    };

    handleToggle = (id: string) => {
        this.setState(s => {
            const newExpanded = new Set(s.expandedIds);
            if (newExpanded.has(id)) {
                newExpanded.delete(id);
            } else {
                newExpanded.add(id);
            }
            return { expandedIds: newExpanded };
        });
    };

    handleMouseMove = (e: React.MouseEvent, id: string) => {
        // Only update selection if the mouse actually moved
        // This prevents scroll-triggered "hover" from changing selection
        const newPos = { x: e.clientX, y: e.clientY };

        if (this.lastMousePosition === null ||
            Math.abs(newPos.x - this.lastMousePosition.x) > 1 ||
            Math.abs(newPos.y - this.lastMousePosition.y) > 1) {
            this.lastMousePosition = newPos;
            this.setState({ selectedId: id });
        }
    };

    handleRef = (id: string, el: HTMLDivElement | null) => {
        if (el) {
            this.itemRefs.set(id, el);
        } else {
            this.itemRefs.delete(id);
        }
    };

    getFilteredLeaves(): Array<{node: RelationshipTaxonomyNode; path: string[]; score: number}> {
        const { query } = this.state;
        if (!query.trim()) return [];

        const results: Array<{node: RelationshipTaxonomyNode; path: string[]; score: number}> = [];
        for (const item of this.leafItems) {
            const labelMatch = fuzzyMatch(item.node.label, query);
            const descMatch = item.node.description ? fuzzyMatch(item.node.description, query) : { match: false, score: 0 };
            let keywordScore = 0;
            if (item.node.keywords) {
                for (const kw of item.node.keywords) {
                    const kwMatch = fuzzyMatch(kw, query);
                    if (kwMatch.match) keywordScore = Math.max(keywordScore, kwMatch.score * 0.8);
                }
            }
            const pathMatch = fuzzyMatch(item.path.join(" "), query);
            const bestScore = Math.max(labelMatch.score, descMatch.score * 0.7, keywordScore, pathMatch.score * 0.5);
            if (bestScore > 0) {
                results.push({ ...item, score: bestScore });
            }
        }
        return results.sort((a, b) => b.score - a.score);
    }

    highlightMatch = (text: string, query: string): React.ReactNode => {
        if (!query) return text;
        const lowerText = text.toLowerCase();
        const lowerQuery = query.toLowerCase();
        const index = lowerText.indexOf(lowerQuery);
        if (index === -1) return text;
        return (
            <>
                {text.slice(0, index)}
                <span style={{ color: "#6df", fontWeight: 600 }}>{text.slice(index, index + query.length)}</span>
                {text.slice(index + query.length)}
            </>
        );
    };

    render() {
        const { x, y, sourceLabel, targetLabel } = this.props;
        const { query, selectedId, expandedIds } = this.state;
        const searchResults = this.getFilteredLeaves();
        const isSearching = query.trim().length > 0;

        const paletteWidth = 380;
        const paletteHeight = 480;

        const containerStyle: React.CSSProperties = {
            width: paletteWidth,
            maxHeight: paletteHeight,
            background: "rgba(20, 22, 28, 0.98)",
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: 13,
            color: "#e0e0e0",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            backdropFilter: "blur(10px)",
            animation: "paletteSlideIn 0.15s ease-out"
        };

        return (
            <foreignObject x={x - paletteWidth / 2} y={y - paletteHeight / 2} width={paletteWidth + 20} height={paletteHeight + 30}>
                <div ref={this.containerRef} style={containerStyle}>
                    {/* Header */}
                    <div style={{ padding: "12px 14px 8px", borderBottom: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                        {sourceLabel && targetLabel && (
                            <div style={{ marginBottom: 8, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                                <span style={{ color: "#6df" }}>{sourceLabel}</span>
                                <span style={{ margin: "0 6px" }}>→</span>
                                <span style={{ color: "#f6d" }}>{targetLabel}</span>
                            </div>
                        )}
                        <div style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: "rgba(255,255,255,0.06)", borderRadius: 8,
                            padding: "8px 12px", border: "1px solid rgba(255,255,255,0.1)"
                        }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.5 }}>
                                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
                                <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <input
                                ref={this.inputRef}
                                type="text"
                                placeholder="Search or browse hierarchy..."
                                value={query}
                                onChange={(e) => this.setState({ query: e.target.value })}
                                style={{
                                    flex: 1, background: "transparent", border: "none",
                                    outline: "none", color: "#fff", fontSize: 14, fontFamily: "inherit"
                                }}
                            />
                            {isSearching && (
                                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                                    {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                                </span>
                            )}
                        </div>

                        {/* Mode indicator */}
                        <div style={{ marginTop: 8, display: "flex", gap: 8, fontSize: 10 }}>
                            <span style={{
                                padding: "3px 8px", borderRadius: 4,
                                background: !isSearching ? "rgba(100,180,255,0.2)" : "rgba(255,255,255,0.05)",
                                color: !isSearching ? "#6bf" : "rgba(255,255,255,0.4)"
                            }}>
                                Browse
                            </span>
                            <span style={{
                                padding: "3px 8px", borderRadius: 4,
                                background: isSearching ? "rgba(100,180,255,0.2)" : "rgba(255,255,255,0.05)",
                                color: isSearching ? "#6bf" : "rgba(255,255,255,0.4)"
                            }}>
                                Search
                            </span>
                        </div>
                    </div>

                    {/* Content */}
                    <div ref={this.listRef} style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
                        {isSearching ? (
                            // Search results (flat list)
                            searchResults.length === 0 ? (
                                <div style={{ padding: "20px 14px", textAlign: "center", color: "rgba(255,255,255,0.4)" }}>
                                    No relationships found
                                </div>
                            ) : (
                                searchResults.map(item => (
                                    <div
                                        key={item.node.id}
                                        ref={(el) => this.handleRef(item.node.id, el)}
                                        style={{
                                            padding: "8px 14px", cursor: "pointer",
                                            display: "flex", alignItems: "center", gap: 10,
                                            background: selectedId === item.node.id ? "rgba(100,180,255,0.15)" : "transparent",
                                            borderLeft: selectedId === item.node.id ? "2px solid #6bf" : "2px solid transparent"
                                        }}
                                        onClick={() => item.node.type && this.props.onSelect(item.node)}
                                        onMouseMove={(e) => this.handleMouseMove(e, item.node.id)}
                                    >
                                        <div style={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.6)" }}>
                                            <svg width="14" height="14" viewBox="0 0 12 12">{item.node.icon}</svg>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 500 }}>{this.highlightMatch(item.node.label, query)}</div>
                                            {item.node.description && (
                                                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>
                                                    {this.highlightMatch(item.node.description, query)}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
                                            {item.path.slice(0, -1).join(" › ")}
                                        </div>
                                    </div>
                                ))
                            )
                        ) : (
                            // Hierarchical tree view
                            this.taxonomy.map(node => (
                                <TreeItem
                                    key={node.id}
                                    node={node}
                                    depth={0}
                                    expandedIds={expandedIds}
                                    selectedId={selectedId}
                                    onToggle={this.handleToggle}
                                    onSelect={this.props.onSelect}
                                    onMouseMove={this.handleMouseMove}
                                    onRef={this.handleRef}
                                    query={query}
                                    highlightMatch={this.highlightMatch}
                                />
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{
                        padding: "8px 14px", borderTop: "1px solid rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.02)", display: "flex",
                        justifyContent: "space-between", fontSize: 11, color: "rgba(255,255,255,0.4)"
                    }}>
                        <div style={{ display: "flex", gap: 12 }}>
                            <span>
                                <span style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>↑↓</span>
                                <span style={{ marginLeft: 4 }}>navigate</span>
                            </span>
                            {!isSearching && (
                                <span>
                                    <span style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>←→</span>
                                    <span style={{ marginLeft: 4 }}>collapse/expand</span>
                                </span>
                            )}
                            <span>
                                <span style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>⏎</span>
                                <span style={{ marginLeft: 4 }}>select</span>
                            </span>
                        </div>
                        <div>
                            <span style={{ background: "rgba(255,255,255,0.1)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>
                                esc
                            </span>
                            <span style={{ marginLeft: 4 }}>cancel</span>
                        </div>
                    </div>
                </div>
                <style>{`
                    @keyframes paletteSlideIn {
                        from { opacity: 0; transform: translateY(-8px) scale(0.96); }
                        to { opacity: 1; transform: translateY(0) scale(1); }
                    }
                `}</style>
            </foreignObject>
        );
    }
}

export { RelationshipCommandPaletteHierarchical, exampleDeepTaxonomy };
